import { useState, useEffect, useRef } from "react"
import { ChevronsUpDown, X, Search } from "lucide-react"
import { getUsers } from "@/lib/api"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { toast } from "sonner"

export interface UserOption {
  id: string
  first_name: string
  last_name: string
  email?: string
  phone_number?: string
  selected_activities?: string[]
}

interface UserComboboxProps {
  value: UserOption | null
  onSelect: (user: UserOption | null) => void
  placeholder?: string
  className?: string
}

export function UserCombobox({
  value,
  onSelect,
  placeholder = "Search users...",
  className,
}: UserComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [results, setResults] = useState<UserOption[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!search.trim() || search.length < 2) {
      setResults([])
      return
    }

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true)
      try {
        const users = await getUsers(search)
        setResults(users)
      } catch {
        toast.error("Failed to search users")
        setResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [search])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  if (value) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <span className="font-medium">
            {value.first_name} {value.last_name}
          </span>
          <button
            onClick={() => onSelect(null)}
            className="ml-1 rounded-sm opacity-70 hover:opacity-100"
          >
            <X className="size-3.5" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className="w-[240px] justify-between text-muted-foreground"
        onClick={() => setOpen(!open)}
      >
        <Search className="mr-2 size-4" />
        {placeholder}
        <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div className="absolute z-50 mt-1 w-[300px] rounded-md border bg-popover shadow-md">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Type name, email, or phone..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {isSearching && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Searching...
                </div>
              )}
              {!isSearching && search.length >= 2 && results.length === 0 && (
                <CommandEmpty>No users found.</CommandEmpty>
              )}
              {!isSearching && results.length > 0 && (
                <CommandGroup>
                  {results.map((user) => {
                    const contact = user.email || user.phone_number || ""
                    return (
                      <CommandItem
                        key={user.id}
                        value={user.id}
                        onSelect={() => {
                          onSelect(user)
                          setOpen(false)
                          setSearch("")
                          setResults([])
                        }}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {user.first_name} {user.last_name}
                          </span>
                          {contact && (
                            <span className="text-xs text-muted-foreground">
                              {contact}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  )
}
